import { z } from 'zod';
import { AdjustmentStateSchema, type AdjustmentState } from './forecast-adjustment';

/**
 * Forecast-adjustment review state machine.
 *
 *            ┌── re-submit ──┐
 *            ↓                │
 *  NEW ──→ AM_REVIEWED ──→ ACCEPTED
 *            │                 │
 *            └──→ REJECTED ←───┘
 */
export const TRANSITIONS: Record<AdjustmentState, AdjustmentState[]> = {
  NEW: ['AM_REVIEWED', 'REJECTED'],
  AM_REVIEWED: ['ACCEPTED', 'REJECTED', 'NEW'],
  ACCEPTED: [],
  REJECTED: ['NEW'], // allow re-submit / re-open
};

export function canTransition(from: AdjustmentState, to: AdjustmentState): boolean {
  return TRANSITIONS[from]?.includes(to) ?? false;
}

export const ReviewActionSchema = z.enum(['REVIEW', 'ACCEPT', 'REJECT', 'REOPEN']);
export type ReviewAction = z.infer<typeof ReviewActionSchema>;

export const ACTION_TO_STATE: Record<ReviewAction, AdjustmentState> = {
  REVIEW: 'AM_REVIEWED',
  ACCEPT: 'ACCEPTED',
  REJECT: 'REJECTED',
  REOPEN: 'NEW',
};

export const ReviewDecisionSchema = z.object({
  id: z.string(),
  adjustment_id: z.string(),
  from_state: AdjustmentStateSchema,
  to_state: AdjustmentStateSchema,
  action: ReviewActionSchema,
  decided_by: z.string(),
  decided_at: z.string(),
  note: z.string().default(''),
});
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

/**
 * UI-friendly progress steps for the StatePipeline component.
 * Order matters — must match the visual rail left → right.
 */
export const STATE_PIPELINE: { state: AdjustmentState; label: string }[] = [
  { state: 'NEW', label: 'Chờ duyệt' },
  { state: 'AM_REVIEWED', label: 'Đã review' },
  { state: 'ACCEPTED', label: 'Chốt' },
];

export function pipelineStepFor(state: AdjustmentState): number {
  const idx = STATE_PIPELINE.findIndex((s) => s.state === state);
  if (idx >= 0) return idx;
  if (state === 'REJECTED') return -1;
  return 0;
}
